import React, { useEffect, useRef } from 'react';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SignaturePad from 'react-signature-canvas';

interface SignatureCanvasProps {
	onSignatureChange: (signature: string | null, isEmpty: boolean) => void;
	disabled?: boolean;
}

const CANVAS_HEIGHT = '200px';

const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
	onSignatureChange,
	disabled = false,
}) => {
	const sigPadRef = useRef<SignaturePad>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const strokeDataRef = useRef<ReturnType<SignaturePad['toData']>>([]);

	const clearSignature = () => {
		if (disabled) return;
		sigPadRef?.current?.clear();
		strokeDataRef.current = [];
		onSignatureChange(null, true);
	};

	const captureSignature = () => {
		return (
			sigPadRef?.current?.getTrimmedCanvas()?.toDataURL('image/png') || null
		);
	};

	useEffect(() => {
		const restoreSignature = () => {
			if (!sigPadRef.current || !strokeDataRef.current.length) return;

			const signaturePad = sigPadRef.current;
			signaturePad.clear();
			signaturePad.fromData(strokeDataRef.current);
			onSignatureChange(captureSignature(), false);
		};

		const resizeCanvas = () => {
			if (!sigPadRef.current || !wrapperRef.current) return;

			const signaturePad = sigPadRef.current;
			const canvas = signaturePad.getCanvas();
			const wrapper = wrapperRef.current;
			const pixelRatio = Math.max(window.devicePixelRatio || 1, 1);
			const nextWidth = Math.max(
				Math.floor(wrapper.clientWidth * pixelRatio),
				1
			);
			const nextHeight = Math.max(
				Math.floor(wrapper.clientHeight * pixelRatio),
				1
			);

			canvas.style.width = '100%';
			canvas.style.height = CANVAS_HEIGHT;
			canvas.style.borderRadius = '15px';

			if (canvas.width === nextWidth && canvas.height === nextHeight) return;

			canvas.width = nextWidth;
			canvas.height = nextHeight;
			canvas.getContext('2d')?.scale(pixelRatio, pixelRatio);
			restoreSignature();
		};

		resizeCanvas();
		window.addEventListener('resize', resizeCanvas);
		window.addEventListener('orientationchange', resizeCanvas);
		window.addEventListener('pageshow', restoreSignature);
		document.addEventListener('visibilitychange', restoreSignature);

		return () => {
			window.removeEventListener('resize', resizeCanvas);
			window.removeEventListener('orientationchange', resizeCanvas);
			window.removeEventListener('pageshow', restoreSignature);
			document.removeEventListener('visibilitychange', restoreSignature);
		};
	}, [onSignatureChange]);

	return (
		<div
			ref={wrapperRef}
			className="bg-white w-100 mx-auto"
			style={{
				border: 'solid 1px #00000042',
				borderRadius: '15px',
				maxWidth: '1000px',
				position: 'relative',
				height: CANVAS_HEIGHT,
				overflow: 'hidden',
			}}>
			<FontAwesomeIcon
				icon={faRotateRight}
				className="m-2"
				style={{
					cursor: 'pointer',
					height: '20px',
					width: '20px',
					position: 'absolute',
					right: '0',
					top: '0',
					color: disabled ? '#98a2b3' : '#03355C',
					opacity: disabled ? 0.6 : 1,
					zIndex: 10,
				}}
				onClick={clearSignature}
				title="Clear signature"
			/>
			<SignaturePad
				ref={sigPadRef}
				clearOnResize={false}
				penColor={disabled ? 'rgba(3, 53, 92, 0.45)' : '#000'}
				canvasProps={{
					style: {
						width: '100%',
						height: CANVAS_HEIGHT,
						touchAction: 'none',
						WebkitUserSelect: 'none',
						WebkitTouchCallout: 'none',
						pointerEvents: disabled ? 'none' : 'auto',
						opacity: disabled ? 0.7 : 1,
					},
				}}
				onBegin={() => {
					if (disabled) return;
					onSignatureChange(null, false);
				}}
				onEnd={() => {
					if (disabled) return;
					strokeDataRef.current = sigPadRef.current?.toData() ?? [];
					const signature = captureSignature();
					onSignatureChange(signature, false);
				}}
			/>
		</div>
	);
};

export default SignatureCanvas;
